import { extendTheme } from "@chakra-ui/react";

const theme = extendTheme({
    fonts: {
        body: "Inter, sans-serif",
        heading: "Inter, sans-serif",
    },
    colors: {
        brand: {
            50: "#EAF2F8",
            100: "#CFE3F0",
            200: "#9FC7E1",
            300: "#6FA9D0",
            400: "#3F86B7",
            500: "#256793",
            600: "#154360",
            700: "#0F3650",
            800: "#0B2536",
            900: "#071722",
        },
        accent: {
            50: "#E6FBF8",
            100: "#C1F4EC",
            200: "#8FE9DC",
            300: "#5CDBC9",
            400: "#2FC9B4",
            500: "#14B8A6",
            600: "#0D9488",
            700: "#0A776E",
        },
        surface: {
            50: "#F7FAFC",
            100: "#F4F7FB",
            200: "#EAF2F8",
        },
    },
    radii: {
        md: "10px",
        lg: "14px",
        xl: "18px",
        "2xl": "22px",
    },
    shadows: {
        soft: "0 1px 2px rgba(15, 54, 80, 0.06), 0 1px 3px rgba(15, 54, 80, 0.04)",
        card: "0 8px 24px -8px rgba(15, 54, 80, 0.14)",
        popover: "0 16px 40px -12px rgba(15, 54, 80, 0.28)",
    },
    styles: {
        global: {
            "html, body": {
                fontFamily: "Inter, sans-serif",
                bg: "surface.100",
                color: "gray.800",
            },
            a: {
                textDecoration: "none",
            },
            "*::selection": {
                background: "brand.100",
            },
        },
    },
    components: {
        Button: {
            baseStyle: {
                fontWeight: "600",
                borderRadius: "lg",
                transition: "all 0.15s ease-out",
                _focusVisible: {
                    boxShadow: "0 0 0 3px var(--chakra-colors-brand-200)",
                },
            },
        },
        IconButton: {
            baseStyle: {
                borderRadius: "lg",
                transition: "all 0.15s ease-out",
            },
        },
        Input: {
            baseStyle: {
                field: {
                    borderRadius: "lg",
                    transition: "all 0.15s ease-out",
                    _focus: {
                        borderColor: "brand.600",
                        boxShadow: "0 0 0 1px var(--chakra-colors-brand-600)",
                    },
                },
            },
        },
        Textarea: {
            baseStyle: {
                borderRadius: "lg",
                transition: "all 0.15s ease-out",
                _focus: {
                    borderColor: "brand.600",
                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-600)",
                },
            },
        },
        Select: {
            baseStyle: {
                field: {
                    borderRadius: "lg",
                    transition: "all 0.15s ease-out",
                    _focus: {
                        borderColor: "brand.600",
                        boxShadow: "0 0 0 1px var(--chakra-colors-brand-600)",
                    },
                },
            },
        },
        Modal: {
            baseStyle: {
                dialog: {
                    borderRadius: "2xl",
                    boxShadow: "card",
                },
            },
        },
        Menu: {
            baseStyle: {
                list: {
                    borderRadius: "xl",
                    boxShadow: "popover",
                    border: "1px solid",
                    borderColor: "gray.100",
                    py: 2,
                },
                item: {
                    borderRadius: "md",
                    mx: 1,
                    w: "calc(100% - 8px)",
                },
            },
        },
        Tooltip: {
            baseStyle: {
                borderRadius: "md",
                bg: "brand.800",
                px: 3,
                py: 1.5,
                fontSize: "xs",
            },
        },
        Badge: {
            baseStyle: {
                borderRadius: "md",
                fontWeight: "600",
                letterSpacing: "0.02em",
            },
        },
        Alert: {
            baseStyle: {
                container: {
                    borderRadius: "xl",
                },
            },
        },
        Tabs: {
            defaultProps: {
                colorScheme: "brand",
            },
        },
        Table: {
            baseStyle: {
                table: {
                    // position:sticky on th/td doesn't paint reliably (mobile
                    // Safari especially) when combined with the default
                    // border-collapse:collapse - needed for the sticky "#"
                    // column to stay opaque instead of showing scrolled
                    // content bleeding through behind it.
                    borderCollapse: "separate",
                    borderSpacing: 0,
                },
                th: {
                    fontWeight: "700",
                    textTransform: "none",
                    fontSize: "13px",
                    letterSpacing: "0.01em",
                    color: "brand.700",
                    borderBottom: "2px solid",
                    borderColor: "brand.100",
                },
                td: {
                    fontSize: "14px",
                    color: "gray.700",
                    borderBottom: "1px solid",
                    borderColor: "gray.100",
                },
            },
            variants: {
                striped: {
                    tbody: {
                        tr: {
                            "&:nth-of-type(odd)": {
                                "th, td": {
                                    borderBottomWidth: "1px",
                                },
                                td: {
                                    background: "surface.100",
                                },
                            },
                        },
                    },
                },
            },
        },
    },
});

export default theme;
